import { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';

import {
  ACTIVITIES_RESPONSE_VARIANT_IMAGE,
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
  COMPONENT_LAYOUT_GRID,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import { DIRECTION_LEFT } from 'webclient.constants';
import customNanoId from 'lib/nanoid/nanoid';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  Sortable,
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { SettingsProps } from './multiple-choice-settings.constants';
import {
  ANSWER_LABELS,
  MultipleChoiceOption,
} from '../multiple-choice.constants';
import { useMultipleChoiceUpdate } from './multiple-choice-settings.hooks';
import { Box } from 'approot/shared/design-system/box/box.style';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import {
  getMultipleChoiceImageContainerDimensions,
  updateOptionImageData,
} from './multiple-choice-settings.utils';
import {
  getDefaultRowAndGridLayout,
  getImageCropData,
} from '../multiple-choice.utils';
import ComponentImageModal from '../../shared/component-image-modal/component-image-modal';
import { PStyled } from 'approot/shared/styles/typography.style';
import { FILL } from '../../image/image-crop.constants';
import ComponentAltTextLabel from '../../shared/component-alt-text/component-alt-text-label';

const MultipleChoiceSettingsContent = ({
  sectionId,
  id,
  type,
  sort,
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  version,
}: SettingsProps) => {
  const [itemId, setItemId] = useState<string | null>(null);

  const { handleUpdate, loading } = useMultipleChoiceUpdate({
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    language: LANGUAGE_CODE_EN_AU,
    version,
  });

  const { options } = properties.multipleChoice;
  const sortedOptions = useMemo(() => sortBy(options, 'sort'), [options]);

  const isImage =
    properties.multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_IMAGE;
  const isText =
    properties.multipleChoice.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT;
  const isTextAndImage =
    properties.multipleChoice.variant ===
    ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE;
  const isShowLetter = properties.multipleChoice.showOptionLetter;

  const selectedItem = useMemo(
    () => sortedOptions.find(o => o.itemId === itemId),
    [itemId, sortedOptions]
  );

  const selectedItemDimensions = useMemo(
    () => getMultipleChoiceImageContainerDimensions(selectedItem?.itemId),
    [selectedItem]
  );

  return (
    <SettingsGroup as="fieldset" marginBottom={0}>
      <ScreenReaderOnly as="legend">
        Multiple Choice Settings Content
      </ScreenReaderOnly>
      <SettingsGroup>
        <SettingsLabel>Choices Type</SettingsLabel>
        <CheckableSingle
          name="variant"
          options={[
            { label: 'Text Only', value: ACTIVITIES_RESPONSE_VARIANT_TEXT },
            { label: 'Image Only', value: ACTIVITIES_RESPONSE_VARIANT_IMAGE },
            {
              label: 'Text and Image',
              value: ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
            },
          ]}
          selected={properties.multipleChoice.variant}
          onSelect={variant =>
            handleUpdate({
              ...properties,
              layout: {
                __typename: 'ComponentLayout',
                display: COMPONENT_LAYOUT_GRID,
              },
              multipleChoice: {
                ...properties.multipleChoice,
                variant,
              },
              __typename: 'MultipleChoiceComponentProperties',
            })
          }
        />
      </SettingsGroup>

      <SettingsHr $marginBottom={24} />

      {selectedItem && selectedItemDimensions && (
        <ComponentImageModal
          loading={loading}
          containerDimensions={{
            width: selectedItemDimensions.width,
            height: selectedItemDimensions.height,
          }}
          image={selectedItem.image || undefined}
          altText={selectedItem.image?.alt}
          sourceUrl={selectedItem.sourceUrl}
          cropData={getImageCropData(
            selectedItem.imageLayout,
            selectedItem.cropData
          )}
          closeModal={() => setItemId(null)}
          onUpdateData={data => {
            handleUpdate(
              {
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  options: updateOptionImageData({
                    options,
                    id: selectedItem.itemId,
                    altText: data.altText,
                    cropData: data.cropData,
                    imageLayout: data.imageLayout,
                    image: data.image || null,
                    sourceUrl: data.sourceUrl,
                  }),
                },
              },
              {
                isSaveImmediate: true,
                onCompleted: () => {
                  setItemId(null);
                },
              }
            );
          }}
        />
      )}

      <SettingsGroup marginBottom={12}>
        <Sortable<MultipleChoiceOption>
          list={sortedOptions}
          renderItem={(item, index) => {
            const outerItem = sortedOptions.find(s => s.itemId === item.itemId);
            return (
              <SortableItem key={item.itemId}>
                <SortableHeader>
                  {sortedOptions.length > 2 && (
                    <DeleteButtonIcon
                      $isSmall
                      aria-label={`Delete option ${index + 1}`}
                      data-selector="icon-delete"
                      onClick={() =>
                        handleUpdate({
                          ...properties,
                          multipleChoice: {
                            ...properties.multipleChoice,
                            rowsGridLayout: getDefaultRowAndGridLayout(
                              options.length - 1
                            ),
                            options: options.filter(
                              o => o.itemId !== item.itemId
                            ),
                          },
                        })
                      }
                    />
                  )}
                </SortableHeader>
                <SortableContent>
                  {(isImage || isTextAndImage) && (
                    <Box
                      $justifyContent="space-between"
                      $alignItems="center"
                      $marginBottom={isTextAndImage ? 12 : 0}
                      data-selector="box-thumbnail-and-alt-text"
                    >
                      <Box
                        $gap={8}
                        $alignItems="center"
                        data-selector="box-thumbnail-and-label"
                      >
                        <ImageThumbnailStatic
                          imageUrl={outerItem?.image?.original || ''}
                          width={48}
                          data-selector="image-thumbnail-static"
                        />
                        {isShowLetter && (
                          <strong>{ANSWER_LABELS[index]}</strong>
                        )}
                        <ComponentAltTextLabel
                          hasAltText={!!outerItem?.image?.alt}
                        />
                      </Box>
                      <SecondaryButton
                        type="button"
                        $isSmall
                        onClick={() => {
                          if (outerItem) setItemId(item.itemId);
                        }}
                      >
                        Edit
                      </SecondaryButton>
                    </Box>
                  )}
                  {isText && isShowLetter && (
                    <PStyled marginBottom={4}>
                      <strong>{ANSWER_LABELS[index]}</strong>
                    </PStyled>
                  )}
                  {(isText || isTextAndImage) && (
                    <AutoResizingTextArea
                      data-selector={`choice-text-${index}`}
                      id={`field-${index}`}
                      name={`field-${index}`}
                      maxLength={150}
                      showCounter
                      rows={1}
                      placeholder={`Choice ${index + 1}`}
                      initialValue={outerItem?.text || ''}
                      onChange={event =>
                        handleUpdate({
                          ...properties,
                          multipleChoice: {
                            ...properties.multipleChoice,
                            options: options.map(option =>
                              option.itemId === item.itemId
                                ? {
                                    ...option,
                                    text: event.target.value,
                                  }
                                : option
                            ),
                          },
                        })
                      }
                    />
                  )}
                </SortableContent>
              </SortableItem>
            );
          }}
          onSortChange={({ sourceIndex, destinationIndex, direction }) => {
            if (sourceIndex !== destinationIndex) {
              const newSort = getReorderSort<MultipleChoiceOption>(
                sortedOptions,
                destinationIndex,
                direction
              );
              handleUpdate({
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  options: sortedOptions.map((option, index) =>
                    sourceIndex === index
                      ? {
                          ...option,
                          sort: newSort,
                        }
                      : option
                  ),
                },
              });
            }
          }}
        />
      </SettingsGroup>
      {options.length < 12 && (
        <Box $marginBottom={24}>
          <SecondaryButton
            data-selector="btn-add-choice"
            type="button"
            onClick={() =>
              handleUpdate({
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  rowsGridLayout: getDefaultRowAndGridLayout(
                    options.length + 1
                  ),
                  options: [
                    ...options,
                    {
                      itemId: customNanoId(),
                      sort: getReorderSort(
                        sortedOptions || [],
                        sortedOptions.length - 1,
                        DIRECTION_LEFT
                      ),
                      correctAnswerBoolean: false,
                      text: null,
                      image: null,
                      imageUrls: null,
                      imageLayout: FILL,
                      cropData: null,
                      sourceUrl: null,
                      __typename: 'MultipleChoiceOption',
                    },
                  ],
                },
              })
            }
          >
            Add Choice
          </SecondaryButton>
        </Box>
      )}
    </SettingsGroup>
  );
};

export default MultipleChoiceSettingsContent;
